import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import FormDefinitionsTable from '../components/forms/FormDefinitionsTable.jsx';
import NewFormModal from '../components/forms/NewFormModal.jsx';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import useForms from '../hooks/useForms.js';
import LoadingView from '../../shared/components/LoadingView';
import FilterFieldSelect from '../components/forms/FilterFieldSelect';

const IN_USE_OPTIONS = [
  { value: 'all', label: 'All forms' },
  { value: '1', label: 'In use' },
  { value: '0', label: 'Not in use' },
];

export default function OrganizationFormsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  useDocumentTitle('Forms – Organization settings – Maintella');
  const fileInputRef = useRef(null);
  const { forms, loading, error, uploadFormDefinition } = useForms(searchParamsKey);
  const [uploadError, setUploadError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const inUse = searchParams.get('in_use') || 'all';

  const setInUseFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('in_use');
    else next.set('in_use', value);
    setSearchParams(next, { replace: true });
  };

  const formFilterExtras = useMemo(
    () => (
      <FilterFieldSelect
        id="forms-filter-in-use"
        label="Usage"
        value={inUse}
        onChange={(e) => setInUseFilter(e.target.value)}
        options={IN_USE_OPTIONS}
      />
    ),
    // searchParamsKey keeps URL-driven handlers in sync; value props track filter state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inUse, searchParamsKey]
  );

  const onFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      setUploadError('');
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        await uploadFormDefinition(parsed);
      } catch (err) {
        setUploadError(err.message || 'Upload failed');
      }
    },
    [uploadFormDefinition]
  );

  const openFilePicker = () => fileInputRef.current?.click();
  const openNewFormModal = () => {
    setUploadError('');
    setShowNewModal(true);
  };

  const createAndOpenEditor = useCallback(
    async ({ slug, title }) => {
      setCreating(true);
      setUploadError('');
      try {
        const created = await uploadFormDefinition({
          slug,
          title,
          description: '',
          json_schema: {
            type: 'object',
            properties: {},
          },
          ui_schema: {},
          defaults: {},
          meta: {},
        });
        setShowNewModal(false);
        if (created?.id) {
          navigate(`/admin/forms/${created.id}/edit`);
          return;
        }
        throw new Error('Form created, but id was missing from response.');
      } catch (err) {
        setUploadError(err.message || 'Create failed');
      } finally {
        setCreating(false);
      }
    },
    [navigate, uploadFormDefinition]
  );

  return (
    <>
      <ObjectTopBar
        title="Forms"
        primaryBtn={{
          btnText: 'New Form',
          btnIcon: 'bi-plus-lg',
          variant: 'primary',
          onClick: openNewFormModal,
        }}
        extra={
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={openFilePicker}>
            <i className="bi bi-upload me-1" />
            Upload JSON
          </button>
        }
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="d-none"
        onChange={onFile}
      />

      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Form definitions</h6>
          <p className="text-muted small mb-0">
            Forms available to your organization include shared defaults and definitions you upload here.
            Uploads are matched by <code>slug</code> and stored for your organization only.
          </p>
        </div>

        {loading && forms.length === 0 ? (
          <LoadingView
            symbolClass="bi-file-earmark-text"
            title="Loading forms"
            subtitle="Fetching form definitions for your organization."
            className="py-5"
          />
        ) : (
          <>
            {(error || uploadError) && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                {error || uploadError}
              </div>
            )}
            <FormDefinitionsTable
              items={forms}
              filterExtras={formFilterExtras}
              onOpenForm={(form) => navigate(`/admin/forms/${form.id}/edit`)}
            />
          </>
        )}
      </div>
      <NewFormModal
        show={showNewModal}
        onClose={() => {
          setShowNewModal(false);
          setUploadError('');
        }}
        onCreate={createAndOpenEditor}
        creating={creating}
        error={uploadError}
      />
    </>
  );
}
